import { useState, useEffect, ReactNode } from 'react';
import { RewardScene, RewardType, ExpRecord, ExpCardProps } from '@/types';
import { EventLayout } from '@/layouts/event-layout';
import { replaceResource } from '@/utils';
import { useReward } from '@/contexts/reward-context';
import { ArrowDownIcon } from './svg-icons';

const ExpCard = ({ data }: ExpCardProps) => {
	const { labels } = useReward();

	return (
		<div className="reward-exp-record">
			<div className="reward-exp-record__img">
				<i className={{
					[RewardType.Betting]: 'reward-iconcolor-turnover',
					[RewardType.Parlay]: 'reward-iconcolor-ticket',
					[RewardType.Streamer]: 'reward-iconcolor-counting',
					[RewardType.Survey]: 'reward-iconcolor-survey',
					[RewardType.LevelUp]: 'reward-iconcolor-lock',
					[RewardType.BookedBet]: 'reward-iconcolor-fast'
				}[data.RewardType]}
				/>
			</div>
			<div className="reward-exp-record__info">
				<div className="reward-exp-record__title">
					{data.RewardType !== RewardType.LevelUp ? labels?.[data.RewardName] : labels?.lbl_RPUnlock}
				</div>
				<div className="reward-exp-record__text">
					<span className="reward-text">
						{data.RewardType === RewardType.LevelUp ?
							labels?.[data.VipName] :
							replaceResource(labels?.[data.EventDescription] ?? '', `${data.Level}`)}
					</span>
				</div>
				{
					data.CompleteDate ?
						<div className="reward-exp-record__text">
							<span className="reward-text">{data.CompleteDate.replace(/^(\d{4})-(\d{2})-(\d{2})T.+/, '$1.$2.$3')}</span>
						</div> : null
				}
			</div>
			{
				data.RewardType === RewardType.BookedBet ? (
					<div className="reward-coin">
						<div className="reward-coin__icon">
							<i className="reward-iconcolor-shop-coin"></i>
						</div>
						<div className="reward-coin__text">+100</div>
					</div>
				) :
					data.Exp > 0 ? 
						<div className="reward-exp-record__result">
							<div className="reward-exp-point">
								<div className="reward-exp-point__icon">
									<i className="reward-iconcolor-exp-star" />
								</div>
								<div className="reward-exp-point__text">{`+${data.Exp}`}</div>
							</div>
						</div> : null
			}
		</div>
	);
};
export const ExpHistory = () => {
	const { getHistory } = useReward();
	const [expHistory, setExpHistory] = useState<ExpRecord[]>([]);

	useEffect(() => {
		if (getHistory) {
			getHistory().then((records: ExpRecord[]) => {
				const showRecords = records.filter(record => {
					return record.RewardType != RewardType.SignIn;
				});
				setExpHistory(showRecords);
			});
		}
	}, []);

	return (
		<EventLayout scene={RewardScene.ExpHistory}>
			{
				expHistory.length == 0 ?
					<HistoryEmpty /> :
					<HistoryContent expHistory={expHistory} />
			}
		</EventLayout>
	);
};

const HistoryEmpty = () => {
	const { labels } = useReward();

	return (
		<div className="reward-empty">
			<div className="reward-empty__img reward-empty__img--record" />
			<div className="reward-empty__text">{labels?.lbl_RPExperienceEmpty}</div>
		</div>
	);
};

const HistoryContent = ({
	expHistory
}: {
	expHistory: ExpRecord[]
}) => {
	const { labels } = useReward();
	const [displayingRecordCount, setDisplayingRecordCount] = useState(expHistory.length <=20 ? expHistory.length : 20);
	const [hasMore, setHasMore] = useState(displayingRecordCount < expHistory.length);

	useEffect(() => {
		setDisplayingRecordCount(expHistory.length <=20 ? expHistory.length : 20);
		setHasMore(20 < expHistory.length);
	}, [expHistory]);

	const moreRecords = () => {
		const fullLength = expHistory.length;
		let lengthIncreaseTo = displayingRecordCount + 20;
		if (lengthIncreaseTo > fullLength) {
			lengthIncreaseTo = fullLength;
		}
		setDisplayingRecordCount(lengthIncreaseTo);
		setHasMore(lengthIncreaseTo < fullLength);
	};

	return (
		<div className="reward-exp-records">
			<div className="reward-exp-records__text">
				<i className="reward-iconcolor-arrangement" />
				{labels?.lbl_RPOrderByLatest}
			</div>
			{
				expHistory
					.slice(0, displayingRecordCount)
					.map((record: ExpRecord, recordIndex: number): ReactNode => {
						return (
							<ExpCard
								key={`${recordIndex}_${record.RewardType}_${record.Level}`}
								data={record}
							/>
						)
					})
			}
			{
				hasMore ? 
					<div className="reward-exp-records__more" onClick={moreRecords}>
						<span className="reward-text">{labels?.lbl_RPMore}</span>
						<ArrowDownIcon />	
					</div> : null
			}
			<div className="reward-exp-records__footer">{labels?.lbl_RPOnlyDisplay}</div>
		</div>
	);
}